import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Fab,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Stack,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  People,
  Home,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Add,
  Event,
  LocationOn,
  CheckCircle,
  Warning,
  Info,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { eventoService, movimentacaoCaixaService, caixaService, caravanaService } from '../services/api';
import OcorrenciasKanban from './OcorrenciasKanban_SIMPLE';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const EventoDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [chartData, setChartData] = useState({
    movimentacoes: [],
    caravanas: [],
    distribuicao: [],
  });

  useEffect(() => {
    loadDashboardData();
  }, [id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const evento = await eventoService.getById(id);
      
      // Dados mock para demonstração
      const mockCaravanas = [
        { id: 1, igreja: { nome: 'Igreja Central', campo: { nome: 'Campo Oeste' } }, quantidade: 45 },
        { id: 2, igreja: { nome: 'Igreja do Vale', campo: { nome: 'Campo Norte' } }, quantidade: 32 },
        { id: 3, igreja: { nome: 'Igreja da Montanha', campo: { nome: 'Campo Sul' } }, quantidade: 28 },
      ];

      const mockMovimentacoes = [
        { id: 1, valor: 1500, data: '2025-01-15' },
        { id: 2, valor: -800, data: '2025-01-16' },
        { id: 3, valor: 2200, data: '2025-01-17' },
      ];

      // Processar dados para gráficos
      const movimentacoesChart = processMovimentacoesData(mockMovimentacoes);
      const caravanasChart = processCaravanasData(mockCaravanas);
      const distribuicaoChart = processDistribuicaoData(mockCaravanas);

      setDashboardData({
        evento,
        caravanas: mockCaravanas,
        movimentacoes: mockMovimentacoes,
        stats: {
          totalCaravanas: mockCaravanas.length,
          totalPessoas: mockCaravanas.reduce((acc, c) => acc + (c.quantidade || 0), 0),
          saldoTotal: mockMovimentacoes.reduce((acc, m) => acc + (m.valor || 0), 0),
          ocupacaoQuartos: 75,
        },
      });

      setChartData({
        movimentacoes: movimentacoesChart,
        caravanas: caravanasChart,
        distribuicao: distribuicaoChart,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processMovimentacoesData = (movimentacoes) => {
    const grouped = movimentacoes.reduce((acc, mov) => {
      const date = new Date(mov.data).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = { date, receitas: 0, despesas: 0 };
      }
      if (mov.valor > 0) {
        acc[date].receitas += mov.valor;
      } else {
        acc[date].despesas += Math.abs(mov.valor);
      }
      return acc;
    }, {});
    return Object.values(grouped);
  };

  const processCaravanasData = (caravanas) => {
    return caravanas.map((c, index) => ({
      nome: c.igreja?.nome || `Caravana ${index + 1}`,
      quantidade: c.quantidade || 0,
    }));
  };

  const processDistribuicaoData = (caravanas) => {
    const distribuicao = caravanas.reduce((acc, c) => {
      const campo = c.igreja?.campo?.nome || 'Outros';
      if (!acc[campo]) {
        acc[campo] = 0;
      }
      acc[campo] += c.quantidade || 0;
      return acc;
    }, {});

    return Object.entries(distribuicao).map(([nome, value]) => ({
      name: nome,
      value,
    }));
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary', trend }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 56, height: 56 }}>
            <Icon />
          </Avatar>
        </Box>
        {trend && (
          <Box display="flex" alignItems="center" mt={2}>
            {trend > 0 ? (
              <TrendingUp color="success" fontSize="small" />
            ) : (
              <TrendingDown color="error" fontSize="small" />
            )}
            <Typography
              variant="body2"
              color={trend > 0 ? 'success.main' : 'error.main'}
              sx={{ ml: 1 }}
            >
              {Math.abs(trend)}% vs último evento
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/eventos')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1">
            {dashboardData?.evento?.nome || 'Dashboard do Evento'}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {dashboardData?.evento?.local?.nome} • {' '}
            {dashboardData?.evento?.data_inicio && 
              new Date(dashboardData.evento.data_inicio).toLocaleDateString()}
          </Typography>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total de Usuários Ativos"
            value="18,765"
            subtitle="+2.6% nos últimos 7 dias"
            icon={People}
            color="primary"
            trend={2.6}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Instalado"
            value="4,876"
            subtitle="+0.2% nos últimos 7 dias"
            icon={Home}
            color="info"
            trend={0.2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Downloads"
            value="678"
            subtitle="-0.1% nos últimos 7 dias"
            icon={TrendingDown}
            color="warning"
            trend={-0.1}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Receita Total"
            value="R$ 45.230"
            subtitle="Todas as movimentações"
            icon={AttachMoney}
            color="success"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Dashboard" />
          <Tab label="Financeiro" />
          <Tab label="Caravanas" />
          <Tab label="Ocorrências" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          {/* Gráfico de Downloads por Sistema */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Download Atual
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Download por sistema operacional
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Mac', value: 15 },
                          { name: 'Windows', value: 55 },
                          { name: 'iOS', value: 20 },
                          { name: 'Android', value: 10 },
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {COLORS.map((color, index) => (
                          <Cell key={`cell-${index}`} fill={color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Typography variant="h4" align="center" sx={{ mt: 2 }}>
                  188,245
                </Typography>
                <Typography variant="body2" align="center" color="textSecondary">
                  Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Gráfico de Área Instalada */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Área Instalada
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  (+43%) em relação ao ano passado
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={[
                        { month: 'Jan', Asia: 20, Europe: 40, Americas: 30 },
                        { month: 'Fev', Asia: 30, Europe: 50, Americas: 35 },
                        { month: 'Mar', Asia: 25, Europe: 45, Americas: 40 },
                        { month: 'Abr', Asia: 35, Europe: 30, Americas: 45 },
                        { month: 'Mai', Asia: 40, Europe: 60, Americas: 50 },
                        { month: 'Jun', Asia: 45, Europe: 55, Americas: 48 },
                        { month: 'Jul', Asia: 50, Europe: 65, Americas: 55 },
                        { month: 'Ago', Asia: 55, Europe: 60, Americas: 52 },
                        { month: 'Set', Asia: 48, Europe: 70, Americas: 58 },
                        { month: 'Out', Asia: 60, Europe: 75, Americas: 65 },
                        { month: 'Nov', Asia: 58, Europe: 72, Americas: 62 },
                        { month: 'Dez', Asia: 65, Europe: 80, Americas: 70 },
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Asia" stackId="a" fill="#00695C" />
                      <Bar dataKey="Europe" stackId="a" fill="#FF9800" />
                      <Bar dataKey="Americas" stackId="a" fill="#2196F3" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Progress Cards */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Progresso do Evento
                </Typography>
                <Stack spacing={3} sx={{ mt: 2 }}>
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Caravanas Confirmadas</Typography>
                      <Typography variant="body2">75%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={75} />
                  </Box>
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Quartos Ocupados</Typography>
                      <Typography variant="body2">60%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={60} color="warning" />
                  </Box>
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Pagamentos Recebidos</Typography>
                      <Typography variant="body2">45%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={45} color="error" />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Activities */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Atividades Recentes
                </Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 32, height: 32 }}>
                      <CheckCircle fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2">Nova caravana confirmada</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Igreja Central - 2 min atrás
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'warning.main', mr: 2, width: 32, height: 32 }}>
                      <Warning fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2">Pagamento pendente</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Igreja do Campo - 15 min atrás
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'info.main', mr: 2, width: 32, height: 32 }}>
                      <Info fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2">Quarto alocado</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Quarto 15A - 1 hora atrás
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Movimentações Financeiras
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={chartData.movimentacoes}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value) => `R$ ${value.toFixed(2)}`} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="receitas"
                      stackId="1"
                      stroke="#4CAF50"
                      fill="#4CAF50"
                      name="Receitas"
                    />
                    <Area
                      type="monotone"
                      dataKey="despesas"
                      stackId="2"
                      stroke="#F44336"
                      fill="#F44336"
                      name="Despesas"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Distribuição por Caravanas
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData.caravanas}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nome" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="quantidade" fill="#2196F3" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <OcorrenciasKanban eventoId={id} />
      </TabPanel>

      {/* FAB para ações rápidas */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default EventoDashboard;